import * as security from '@/common/security'
import toastr from 'toastr'
import store from '@/store'
import dateUtils from '@/common/dateUtils'
import axios from 'axios'
import { AUTH_GET_TOKEN } from '@/store/auth/auth.types'

let _sseEventSource = null
let _sseConnectionFailed = false
let _sseBusyConnectingSince = null
let _sseLastCommsReceived = null
let _sseStreamId = null
let _sseUrl = null
let _sseSubQueue = []
let _sseActiveSubs = []
let _sseRetrySeconds = 0
let _sseRetryInterval = null
let _sseRetryTimeout = null
let _sseHealthCheckInterval = null

/**
 * This must be called to initiate the SSE connection.
 *
 * @param subscription String Can be 'telemetry', 'notify', 'events'
 */
export const sseInit = (subscription) => {
  // We don't want to have duplicates of subscriptions
  // in the array, so only add it if it doesn't already
  // exist.
  if (!_sseSubQueue.includes(subscription)) {
    _sseSubQueue.push(subscription)
  }
  if (!_sseActiveSubs.includes(subscription)) {
    _sseActiveSubs.push(subscription)
  }

  // console.log(`SSE: DEBUG: _sseSubQueue`, _sseSubQueue)
  // console.log(`SSE: DEBUG: _sseActiveSubs`, _sseActiveSubs)

  if (_sseHealthCheckInterval === null) {
    // console.log(`SSE: DEBUG: Starting SSE health check timer`)
    _sseHealthCheckInterval = window.setInterval(sseHealthCheckTimer, 15000)
  }

  // If we are already busy connecting we shouldn't
  // attempt another parallel connection.
  if (_sseBusyConnectingSince !== null) {
    // console.log(`SSE: DEBUG: Waiting for connection, can't establish a new one yet...`)
    return
  }

  if (_sseEventSource !== null) {
    if (_sseStreamId !== null) {
      // console.log(`SSE: DEBUG: Event source and SSE stream ready, running subscription queue...`)
      runSubscriptionQueue()
    }
    return
  }

  _sseUrl = security.user().sse.url
  validateSseUrl()
  console.log(`SSE: Connecting to SSE: ${_sseUrl}`)

  // Setup EventSource instance for our connection
  _sseBusyConnectingSince = dateUtils.now()
  _sseConnectionFailed = false
  _sseEventSource = new EventSource(
    encodeURI(`${_sseUrl}connect`)
  )

  _sseEventSource.onopen = () => {
    console.log(`SSE: EventSource connection to ${_sseUrl} successful, waiting for stream id...`)
  }

  _sseEventSource.onerror = onSseError
  _sseEventSource.onmessage = onSseMessage
}

/**
 * Called when an SSE EventSource error occurs or when we
 * detect that a connection was pending for too long.
 */
const onSseError = () => {
  console.log(`SSE: EventSource connection to ${_sseUrl} failed.`)
  // console.log(`SSE: DEBUG: _sseConnectionFailed`, _sseConnectionFailed)

  // Do not continue if the SSE connection has already failed
  // as it is already being dealt with
  if (_sseConnectionFailed) return

  _sseConnectionFailed = true
  store.commit(`monitoring/monitoringModule/sseHandleError`)

  _sseEventSource.close()
  _sseBusyConnectingSince = null
  _sseLastCommsReceived = null
  _sseEventSource = null
  _sseStreamId = null
  _sseRetrySeconds = 25

  stopAllTimers()
  toastr.clear()
  toastr.error(`Connection down, will retry connecting in ${_sseRetrySeconds} seconds...`, '', {
    'closeButton': true,
    'timeOut': 0,
    'extendedTimeOut': 0
  })

  // console.log(`SSE: DEBUG: Starting retry countdown...`, _sseRetrySeconds)
  _sseRetryInterval = window.setInterval(connectionCountdownTimer, 1000)
}

/**
 * Handles all SSE messages.
 *
 * @param event MessageEvent
 */
const onSseMessage = (event) => {
  const data = JSON.parse(event.data)

  if ('type' in data) {
    switch (data.type) {
      case 'stream_id':
        console.log(`SSE Event: stream_id: ${data.stream_id}`)
        _sseStreamId = data.stream_id
        _sseBusyConnectingSince = null
        _sseLastCommsReceived = dateUtils.now()
        if (_sseConnectionFailed) {
          _sseConnectionFailed = false
          toastr.clear()
          toastr.success('Connection re-established successfully...', '', { timeOut: 5000 })
        }

        // console.log(`SSE: DEBUG: Running subscription queue...`)
        runSubscriptionQueue()
        break

      case 'ping':
        console.log('SSE Event: ping')
        // noinspection JSIgnoredPromiseFromCall
        handlePingEvent()
        break

      case 'gps':
        console.log('SSE Event: gps')
        handleGpsEvent(data)
        break

      case 'event':
        console.log('SSE Event: event')
        handlePoolEvent(data)
        break

      case 'report_status':
        console.log('SSE Event: report_status')
        handleReportStatusEvent(data)
        break

      default:
        console.error('SSE: Unknown SSE type: ' + data.type)
    }
  } else {
    console.error('SSE: Unknown SSE type or no type provided in data.')
  }
}

/**
 * Performs a health check on the SSE connection.
 */
export const sseHealthCheckTimer = () => {
  // console.log(`SSE: DEBUG: sseHealthCheckTimer()`)

  // Make sure the SSE url is still valid, if not
  // the SSE connection will be closed and an exception
  // will be thrown which stops the rest of the health
  // check to be performed.
  validateSseUrl()

  // PRE-CONNECTION STATUS CHECKS
  if (_sseBusyConnectingSince !== null) {
    if (dateUtils.now().diff(_sseBusyConnectingSince, 'seconds') >= 60) {
      // We are actively busy connecting, see for how long.
      // If we've been waiting for more than a minute it means
      // something has gone wrong and we need to stop everything
      // and try again.
      // console.log(`SSE: DEBUG: SSE Health-Check: Connection pending for too long, retrying new connection...`)
      onSseError()
    }

    // Do not continue with additional health checks since we are
    // busy trying to establish a connection.
    return
  }

  // Do not continue if the connection has already failed
  // since the connection retry timers will be running
  if (_sseConnectionFailed) return

  // POST-CONNECTION STATUS CHECKS
  if (_sseLastCommsReceived !== null) {
    // console.log(`SSE: DEBUG: Seconds since last comms`, dateUtils.now().diff(_sseLastCommsReceived, 'seconds'))
  }
  if (_sseLastCommsReceived !== null && dateUtils.now().diff(_sseLastCommsReceived, 'seconds') >= 120) {
    // We have not received any pings or other communications from the server
    // for a while, which means the connection may have failed silently.
    // console.log(`SSE: DEBUG: Health-Check: No comms received from SSE server, retrying new connection...`)
    onSseError()
    // return
  }
}

/**
 * Validate that the SSE url is set, if not the user
 * will be notified, the SSE connection will be closed (if open)
 * and an exception will be thrown.
 */
export const validateSseUrl = () => {
  if (_sseUrl !== null) return

  toastr.clear()
  toastr.error(`Unable to connect to the server due to a misconfiguration. Please try refreshing the page.`, '', {
    'closeButton': true,
    'timeOut': 0,
    'extendedTimeOut': 0
  })

  sseClose()
  throw new Error(`SSE: No SSE url provided.`)
}

/**
 * This must be called to close the SSE connection.
 */
export const sseClose = () => {
  if (_sseEventSource !== null) {
    _sseEventSource.close()
    _sseEventSource = null
    console.log(`SSE: Closed EventSource connection`)
  }

  stopAllTimers()
  _sseConnectionFailed = false
  _sseBusyConnectingSince = null
  _sseLastCommsReceived = null
  _sseStreamId = null
  _sseUrl = null
  _sseSubQueue = []
  _sseActiveSubs = []
  _sseRetrySeconds = 0
}

export const stopAllTimers = () => {
  if (_sseHealthCheckInterval !== null) {
    // console.log(`SSE: DEBUG: Stopping Timer: sseHealthCheckInterval`)
    window.clearInterval(_sseHealthCheckInterval)
    _sseHealthCheckInterval = null
  }

  if (_sseRetryInterval !== null) {
    // console.log(`SSE: DEBUG: Stopping Timer: _sseRetryInterval`)
    window.clearInterval(_sseRetryInterval)
    _sseRetryInterval = null
  }

  if (_sseRetryTimeout !== null) {
    // console.log(`SSE: DEBUG: Stopping Timer: _sseRetryTimeout`)
    window.clearTimeout(_sseRetryTimeout)
    _sseRetryTimeout = null
  }
}

/**
 * Returns the current stream id. Will return null if no stream
 * id have been received yet.
 *
 * @returns String|null
 */
export const getStreamId = () => {
  return _sseStreamId
}

/**
 * Returns the current SSE url to be used.
 *
 * @returns String|null
 */
export const getSseUrl = () => {
  return _sseUrl
}

/**
 * Returns the current event source instance or null if no
 * event source have been created.
 *
 * @returns EventSource|null
 */
export const getSseEventSource = () => {
  return _sseEventSource
}

/**
 * Called to unsubscribe from a specified subscription.
 *
 * @param subscription String Can be 'telemetry', 'notify', 'events'
 */
export const unsubscribe = (subscription) => {
  validateSseUrl()

  // Remove only the active subscription from the array
  _sseActiveSubs = _sseActiveSubs.filter(sub => sub !== subscription)
  if (_sseActiveSubs.length === 0) {
    toastr.clear()
  }

  if (_sseUrl === null || _sseStreamId === null) {
    return Promise.resolve()
  }

  if (subscription === 'telemetry') {
    return sseTelemetryUnSubscribe()
  } else if (subscription === 'notify') {
    return sseNotifyUnSubscribe()
  } else if (subscription === 'events') {
    return sseEventsUnSubscribe()
  }
}

/**
 * Runs subscription requests.
 */
const runSubscriptionQueue = () => {
  _sseSubQueue.forEach((sub) => {
    if (sub === 'telemetry') {
      sseTelemetrySubscribe()
    } else if (sub === 'notify') {
      // noinspection JSIgnoredPromiseFromCall
      sseNotifySubscribe()
    } else if (sub === 'events') {
      // noinspection JSIgnoredPromiseFromCall
      sseEventsSubscribe()
    }
  })

  _sseSubQueue = []
}

/**
 * When a disconnect happens a timer is started up
 * which calls this function once a second. Once
 * the time runs out another connection attempt
 * will be made.
 */
const connectionCountdownTimer = () => {
  // console.log(`SSE: DEBUG: connectionCountdownTimer()`, _sseRetrySeconds)
  if (_sseActiveSubs.length === 0) {
    // If there are no active subscriptions there is nothing
    // for us to do here, so we can cancel the timer and return
    // console.log(`SSE: DEBUG: No active SSE channels, aborting connection.`)
    sseClose()
    return
  }

  _sseRetrySeconds -= 1
  if (_sseRetrySeconds <= 0) {
    // We've reached the end of the count-down,
    // we can retry connecting again...
    retryConnection()
  } else {
    // Update the toastr popup with the seconds
    // remaining.
    updateReconnectToastr(_sseRetrySeconds)
  }
}

/**
 * Retry connecting to the SSE server
 */
const retryConnection = () => {
  stopAllTimers()
  toastr.clear()
  _sseSubQueue = []

  toastr.warning(`Attempting to reconnect...`, '', {
    'closeButton': true,
    'timeOut': 0,
    'extendedTimeOut': 0
  })
  _sseRetryTimeout = window.setTimeout(() => {
    // Wait for a few seconds and then retry connecting...
    toastr.clear()
    _sseActiveSubs.forEach((sub) => {
      sseInit(sub)
    })
  }, 3000)
}

/**
 * Handle a ping event from the SSE server. The client must respond with a
 * "pong" message to indicate it is still online.
 *
 * @return Promise
 */
const handlePingEvent = () => {
  // Safety check to make sure we don't attempt to answer
  // a ping with a pong while we don't have a sse URL.
  if (_sseUrl === null) {
    throw new Error(`SSE: Couldn't respond to pong due to SSE URL not being set.`)
  }

  _sseLastCommsReceived = dateUtils.now()
  const url = `${_sseUrl}pong/${_sseStreamId}`
  console.log(`SSE: Responding to ping check: ${url}`)
  return axios.post(url, {}, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  }).then(function (response) {
    console.log(`SSE: Ping response`, response)
  })
}

/**
 * Handle a 'gps' or position/telemetry message. These are received
 * when the client is subscribed to one or more units/vehicles in order
 * to receive live position updates.
 *
 * @param data Object
 */
const handleGpsEvent = (data) => {
  _sseLastCommsReceived = dateUtils.now()
  store.commit(`monitoring/monitoringModule/handleGpsEvent`, data)
}

/**
 * Handle a pool 'event' message. These are received when the client is
 * subscribed to an event pool.
 *
 * @param data Object
 */
const handlePoolEvent = (data) => {
  _sseLastCommsReceived = dateUtils.now()
  store.commit(`events/eventMonitoringModule/handlePoolEvent`, data)
}

/**
 * Handle a 'report_status' message which is sent when there are
 * reports running on the backend services and the client needs
 * to receive updates on the progress of the report.
 *
 * @param data Object
 */
const handleReportStatusEvent = (data) => {
  store.commit(`reports/reportsModule/handleReportStatusEvent`, data)
}

/**
 * Called when we need to subscribe to telemetry messages.
 */
const sseTelemetrySubscribe = () => {
  validateSseUrl()

  // Note: Unlike the other subscriptions we let the monitoring
  // module handle the logic as it is more complicated and requires
  // state management within the monitoring module.
  store.commit(`monitoring/monitoringModule/handleSseReady`)
}

/**
 * Called when we need to unsubscribe from telemetry messages.
 *
 * @return Promise
 */
const sseTelemetryUnSubscribe = () => {
  validateSseUrl()

  const url = `${_sseUrl}telemetry/unsubscribe/${_sseStreamId}/all`
  console.log(`SSE: Requesting telemetry unsubscription for user: ${url}`)

  // Submit the request to the SSE server.
  return axios.post(url, {}, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  })
}

/**
 * Called when we need to subscribe to notifications.
 *
 * @return Promise
 */
const sseNotifySubscribe = () => {
  validateSseUrl()

  const token = store.getters[AUTH_GET_TOKEN].replace('bearer ', '')
  const url = `${_sseUrl}notify/subscribe/${_sseStreamId}/${token}`
  console.log(`SSE: Requesting notification subscription for user: ${url}`)

  // Submit the request to the SSE server.
  return axios.post(url, {}, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  })
}

/**
 * Called when we need to unsubscribe from notifications.
 *
 * @return Promise
 */
const sseNotifyUnSubscribe = () => {
  validateSseUrl()

  const token = store.getters[AUTH_GET_TOKEN].replace('bearer ', '')
  const url = `${_sseUrl}notify/unsubscribe/${_sseStreamId}/${token}`
  console.log(`SSE: Requesting notification unsubscription for user: ${url}`)

  // Submit the request to the SSE server.
  return axios.post(url, {}, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  })
}

/**
 * Called when we need to subscribe to event for an event pool.
 *
 * @return Promise
 */
const sseEventsSubscribe = () => {
  validateSseUrl()
  return store.dispatch('events/eventMonitoringModule/handleSseReady')
}

/**
 * Called when we need to unsubscribe from events.
 *
 * @return Promise
 */
const sseEventsUnSubscribe = () => {
  validateSseUrl()
  if (getStreamId() !== null) {
    return store.dispatch('events/eventMonitoringModule/postEventUnsubscribe')
  }
}

/**
 * Updates the Connection Down toastr message with a countdown in seconds.
 *
 * @param seconds
 */
const updateReconnectToastr = (seconds) => {
  let toastrDiv = document.getElementById('toast-container')
  if (toastrDiv) {
    let messageDivs = toastrDiv.getElementsByClassName('toast-message')
    for (let index = 0; index < messageDivs.length; index++) {
      if (messageDivs[index].innerHTML.startsWith('Connection down')) {
        messageDivs[index].innerHTML = `Connection down, will retry in ${seconds} seconds...`
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/common/sse.js